import { type FC, Suspense, lazy, type Dispatch, type SetStateAction } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAppSelector } from '../hooks/useReactReduxHooks';

// components
import { PdpRespSidebarDetailsAbout } from './PdpRespSidebarDetailsAbout';
import { PdpRespSidebarDetailsSeller } from './PdpRespSidebarDetailsSeller';
import { PdpRespSidebarDetailsPromise } from './PdpRespSidebarDetailsPromise';
import { PdpRespSidebarDetailsCustomization } from './PdpRespSidebarDetailsCustomization';

// types
import { type PdpRespSidebarDetails_item$key as Item } from './__generated__/PdpRespSidebarDetails_item.graphql';
import { type PdpRespSidebarDetails_user$key as User } from './__generated__/PdpRespSidebarDetails_user.graphql';
import { PdpRespSidebarDetailsShipping } from './PdpRespSidebarDetailsShipping';

const PdpRespSidebarDetailsPricingGuidance = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSidebarDetailsPricingGuidance" */ './PdpRespSidebarDetailsPricingGuidance'
        )
);

const itemFragment = graphql`
    fragment PdpRespSidebarDetails_item on Item {
        ...PdpRespSidebarDetailsAbout_item
        ...PdpRespSidebarDetailsSeller_item
        ...PdpRespSidebarDetailsShipping_item
        ...PdpRespSidebarDetailsCustomization_item
        ...PdpRespSidebarDetailsPricingGuidance_item @include(if: $afterDisplay)
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarDetails_user on User {
        ...PdpRespSidebarDetailsAbout_user
        ...PdpRespSidebarDetailsSeller_user
        ...PdpRespSidebarDetailsPromise_user
        ...PdpRespSidebarDetailsShipping_user
        ...PdpRespSidebarDetailsCustomization_user
    }
`;

export const PdpRespSidebarDetails: FC<{
    item: Item;
    user: User | null;
    skuId: string | null;
    itemQuantity: number | null;
    expandOverridePromiseState: [boolean, Dispatch<SetStateAction<boolean>>];
}> = ({ item: itemRef, user: userRef, skuId, itemQuantity, expandOverridePromiseState }) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef) || null;
    const { afterDisplay } = useAppSelector(relay => relay?.relayVariables?.variables || {});

    return (
        <>
            <PdpRespSidebarDetailsAbout item={item} user={user} skuId={skuId} />
            <PdpRespSidebarDetailsCustomization item={item} user={user} />
            <PdpRespSidebarDetailsShipping item={item} user={user} itemQuantity={itemQuantity} />
            <PdpRespSidebarDetailsSeller item={item} user={user} />
            <PdpRespSidebarDetailsPromise
                user={user}
                expandOverrideState={expandOverridePromiseState}
            />
            {afterDisplay && (
                <Suspense fallback={null}>
                    <PdpRespSidebarDetailsPricingGuidance item={item} skuId={skuId} />
                </Suspense>
            )}
        </>
    );
};
